import React, { useState, useEffect } from 'react';
import { 
  DollarSign, Users, TrendingUp, Settings2, Download, 
  UserPlus, ExternalLink, Check, X, Eye, RefreshCw,
  Percent, Clock, FileText, Copy, Mail, Image, Video, 
  Folder, Upload, Trash2, Send, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MEDIA_CATEGORIES = [
  { id: 'logos', name: 'Logos', icon: Image },
  { id: 'product_images', name: 'Product Images', icon: Image },
  { id: 'banners', name: 'Banners & Ads', icon: Image },
  { id: 'videos', name: 'Videos', icon: Video },
  { id: 'social_stories', name: 'Social Media Stories', icon: Image },
  { id: 'brand_guidelines', name: 'Brand Guidelines', icon: FileText }
];

export const ReferralAffiliateManager = ({ headers }) => {
  const [activeTab, setActiveTab] = useState('referrals');
  const [referrals, setReferrals] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [settings, setSettings] = useState({
    referral_discount_percent: 5,
    referral_commission_percent: 3,
    affiliate_commission_percent: 10,
    affiliate_customer_discount: 5,
    affiliate_cookie_days: 30
  });
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showAffiliateDetails, setShowAffiliateDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Campaign State
  const [campaigns, setCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignCustomers, setCampaignCustomers] = useState([]);
  const [showPointsOverrideDialog, setShowPointsOverrideDialog] = useState(null);
  const [pointsOverrideValue, setPointsOverrideValue] = useState(10);
  
  // Media Library State
  const [mediaAssets, setMediaAssets] = useState([]);
  const [mediaRequests, setMediaRequests] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newMedia, setNewMedia] = useState({
    name: '',
    category: 'logos',
    file_url: '',
    file_type: 'image',
    description: '',
    tags: ''
  });
  
  // Create Affiliate State
  const [showCreateAffiliateDialog, setShowCreateAffiliateDialog] = useState(false);
  const [creatingAffiliate, setCreatingAffiliate] = useState(false);
  const [affiliateForm, setAffiliateForm] = useState({
    name: '',
    email: '',
    city: '',
    province_state: '',
    phone: '',
    affiliate_program_optin: true,
    ftc_optin: false,
    bureau_optin: false,
    tier: 1,
    commission_rate: 10
  });
  
  // Payouts & Store Credits State
  const [affiliateSales, setAffiliateSales] = useState([]);
  const [salesStats, setSalesStats] = useState(null);
  const [storeCredits, setStoreCredits] = useState([]);
  const [storeCreditStats, setStoreCreditStats] = useState(null);
  const [selectedSales, setSelectedSales] = useState([]);
  const [processingPayout, setProcessingPayout] = useState(false);
  const [showIssueCreditDialog, setShowIssueCreditDialog] = useState(null);
  const [creditForm, setCreditForm] = useState({ amount: '', reason: 'Refer-a-Friend reward' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [referralsRes, affiliatesRes, settingsRes, mediaRes, requestsRes, campaignsRes, salesRes, creditsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/referrals`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/affiliates/admin/list`, { headers }).catch(() => ({ data: { affiliates: [], stats: {} } })),
        axios.get(`${API_URL}/api/admin/settings`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/affiliates/media-library`, { headers }).catch(() => ({ data: { assets: [] } })),
        axios.get(`${API_URL}/api/affiliates/admin/media-requests`, { headers }).catch(() => ({ data: { requests: [] } })),
        axios.get(`${API_URL}/api/affiliates/admin/campaigns`, { headers }).catch(() => ({ data: { campaigns: [], stats: {} } })),
        axios.get(`${API_URL}/api/affiliates/admin/sales`, { headers }).catch(() => ({ data: { sales: [], stats: {} } })),
        axios.get(`${API_URL}/api/admin/store-credits`, { headers }).catch(() => ({ data: { credits: [], stats: {} } }))
      ]);
      
      setReferrals(referralsRes.data || []);
      setAffiliates(affiliatesRes.data?.affiliates || []);
      setAffiliateStats(affiliatesRes.data?.stats || null);
      setMediaAssets(mediaRes.data?.assets || []);
      setMediaRequests(requestsRes.data?.requests || []);
      setCampaigns(campaignsRes.data?.campaigns || []);
      setCampaignStats(campaignsRes.data?.stats || null);
      setAffiliateSales(salesRes.data?.sales || []);
      setSalesStats(salesRes.data?.stats || null);
      setStoreCredits(creditsRes.data?.credits || []);
      setStoreCreditStats(creditsRes.data?.stats || null);
      
      if (settingsRes.data) {
        setSettings(prev => ({
          ...prev,
          referral_discount_percent: settingsRes.data.referral_discount_percent || 5,
          referral_commission_percent: settingsRes.data.referral_commission_percent || 3,
          affiliate_commission_percent: settingsRes.data.affiliate_commission_percent || 10,
          affiliate_customer_discount: settingsRes.data.affiliate_customer_discount || 5,
          affiliate_cookie_days: settingsRes.data.affiliate_cookie_days || 30
        }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/settings`, settings, { headers });
      toast.success('Commission settings saved!');
      setShowSettingsDialog(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleToggleReferral = async (ref) => {
    try {
      await axios.put(`${API_URL}/api/admin/referrals/${ref.id}/toggle`, {}, { headers });
      fetchData();
      toast.success(`Referral code ${ref.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to toggle referral');
    }
  };

  const handleSendCreditReminder = async (ref) => {
    try {
      await axios.post(`${API_URL}/api/referrals/send-credit-reminder`, {
        referral_code: ref.code,
        referrer_email: ref.referrer_email,
        referrer_name: ref.referrer_name,
        store_credit_earned: ref.store_credit_earned || ref.pending_referrer_credit || 0,
        total_referrals: ref.total_referrals || 0,
        total_sales: ref.total_sales || 0
      }, { headers });
      toast.success(`Reminder email sent to ${ref.referrer_email}`);
    } catch (error) {
      toast.error('Failed to send reminder email');
      console.error(error);
    }
  };

  const handleUpdateAffiliateStatus = async (affiliateId, status) => {
    try {
      await axios.put(`${API_URL}/api/affiliates/admin/${affiliateId}/status?status=${status}`, {}, { headers });
      fetchData();
      toast.success(`Affiliate status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateAffiliateCommission = async (affiliateId, rate) => {
    try {
      await axios.put(`${API_URL}/api/affiliates/admin/${affiliateId}/commission?commission_rate=${rate}`, {}, { headers });
      fetchData();
      toast.success('Commission rate updated');
    } catch (error) {
      toast.error('Failed to update commission');
    }
  };

  const handleProcessPayout = async (affiliateId, amount) => {
    try {
      await axios.post(`${API_URL}/api/affiliates/admin/${affiliateId}/payout?amount=${amount}`, {}, { headers });
      fetchData();
      toast.success(`Payout of $${amount.toFixed(2)} processed`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payout');
    }
  };

  // Mark selected sales as paid
  const handleMarkSalesAsPaid = async () => {
    if (selectedSales.length === 0) {
      toast.error('Please select at least one sale to mark as paid');
      return;
    }
    
    setProcessingPayout(true);
    try {
      const response = await axios.post(`${API_URL}/api/affiliates/admin/mark-sales-paid`, {
        sale_ids: selectedSales,
        payment_method: 'bank_transfer',
        send_notification: true
      }, { headers });
      
      toast.success(`Marked ${response.data.affiliates_processed} affiliate(s) as paid. Total: $${response.data.total_amount_paid.toFixed(2)}`);
      setSelectedSales([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payouts');
    } finally {
      setProcessingPayout(false);
    }
  };

  // Issue store credit for referral
  const handleIssueStoreCredit = async (referralId, email, name) => {
    if (!creditForm.amount || parseFloat(creditForm.amount) <= 0) {
      toast.error('Please enter a valid credit amount');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/store-credits/issue`, {
        referrer_email: email,
        referrer_name: name,
        amount: parseFloat(creditForm.amount),
        reason: creditForm.reason
      }, { headers });
      
      toast.success(`Store credit of $${creditForm.amount} issued! Code: ${response.data.credit.code}`);
      setShowIssueCreditDialog(null);
      setCreditForm({ amount: '', reason: 'Refer-a-Friend reward' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue store credit');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Media Library Functions
  const handleUploadMedia = async (e) => {
    e.preventDefault();
    if (!newMedia.name || !newMedia.file_url) {
      toast.error('Please fill in name and file URL');
      return;
    }
    
    setUploading(true);
    try {
      const params = new URLSearchParams({
        name: newMedia.name,
        category: newMedia.category,
        file_url: newMedia.file_url,
        file_type: newMedia.file_type,
        description: newMedia.description || '',
        tags: newMedia.tags || ''
      });
      
      await axios.post(`${API_URL}/api/affiliates/admin/media?${params.toString()}`, {}, { headers });
      toast.success('Media asset added!');
      setShowUploadDialog(false);
      setNewMedia({ name: '', category: 'logos', file_url: '', file_type: 'image', description: '', tags: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add media');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMedia = async (assetId) => {
    if (!window.confirm('Delete this asset? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/affiliates/admin/media/${assetId}`, { headers });
      toast.success('Asset deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const handleUpdateRequestStatus = async (requestId, status, notes = '') => {
    try {
      const params = new URLSearchParams({ status });
      if (notes) params.append('admin_notes', notes);
      
      await axios.put(`${API_URL}/api/affiliates/admin/media-requests/${requestId}?${params.toString()}`, {}, { headers });
      toast.success('Request updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  // Create Affiliate Functions
  const handleCreateAffiliate = async (e) => {
    e.preventDefault();
    setCreatingAffiliate(true);
    try {
      const response = await axios.post(`${API_URL}/api/affiliates/admin/create`, {
        name: affiliateForm.name,
        email: affiliateForm.email,
        city: affiliateForm.city,
        province_state: affiliateForm.province_state,
        phone: affiliateForm.phone,
        affiliate_program_optin: affiliateForm.affiliate_program_optin,
        ftc_optin: affiliateForm.ftc_optin,
        bureau_optin: affiliateForm.bureau_optin,
        tier: affiliateForm.tier,
        commission_rate: affiliateForm.commission_rate
      }, { headers });
      
      toast.success(`Affiliate ${affiliateForm.name} created successfully!`);
      resetAffiliateForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create affiliate');
    } finally {
      setCreatingAffiliate(false);
    }
  };

  const resetAffiliateForm = () => {
    setShowCreateAffiliateDialog(false);
    setAffiliateForm({
      name: '',
      email: '',
      city: '',
      province_state: '',
      phone: '',
      affiliate_program_optin: true,
      ftc_optin: false,
      bureau_optin: false,
      tier: 1,
      commission_rate: 10
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#003456] border-t-transparent"></div>
      </div>
    );
  }

  const totalReferralSales = referrals.reduce((acc, r) => acc + (r.total_sales || 0), 0);
  const totalReferralCommission = referrals.reduce((acc, r) => acc + (r.total_commission || 0), 0);

  return (
    <div className="animate-fade-in" data-testid="referral-affiliate-manager">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-[#0a0203]">Referrals & Affiliates</h1>
        <Button onClick={() => setShowSettingsDialog(true)} variant="outline" className="gap-2">
          <Settings2 className="h-4 w-4" />
          Commission Settings
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-gray-100 p-1 rounded-lg flex-wrap">
          <TabsTrigger value="referrals" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Users className="h-4 w-4 mr-2" /> Refer-a-Friend ({referrals.length})
          </TabsTrigger>
          <TabsTrigger value="affiliates" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <TrendingUp className="h-4 w-4 mr-2" /> Affiliate Program ({affiliates.length})
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="admin-campaigns-tab">
            <DollarSign className="h-4 w-4 mr-2" /> Campaigns ({campaigns.length})
          </TabsTrigger>
          <TabsTrigger value="payouts" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="admin-payouts-tab">
            <DollarSign className="h-4 w-4 mr-2" /> Payouts
          </TabsTrigger>
          <TabsTrigger value="media" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="admin-media-tab">
            <Image className="h-4 w-4 mr-2" /> Media Library ({mediaAssets.length})
          </TabsTrigger>
          <TabsTrigger value="legal" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <FileText className="h-4 w-4 mr-2" /> Legal Docs
          </TabsTrigger>
        </TabsList>

        {/* Refer-a-Friend Tab */}
        <TabsContent value="referrals">
          {/* Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <p className="text-sm text-gray-500">Total Referrers</p>
              <p className="text-3xl font-bold text-[#003456]">{referrals.length}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <p className="text-sm text-gray-500">Total Referrals</p>
              <p className="text-3xl font-bold text-[#003456]">
                {referrals.reduce((acc, r) => acc + (r.total_referrals || 0), 0)}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <p className="text-sm text-gray-500">Total Sales</p>
              <p className="text-3xl font-bold text-green-600">${totalReferralSales.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <p className="text-sm text-gray-500">Total Commission</p>
              <p className="text-3xl font-bold text-yellow-600">${totalReferralCommission.toFixed(2)}</p>
            </div>
          </div>

          {/* Current Rates Display */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-800">
                  Customer Discount: <strong>{settings.referral_discount_percent}%</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-800">
                  Referrer Store Credit: <strong>{settings.referral_commission_percent}%</strong>
                </span>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowSettingsDialog(true)}>
              Edit Rates
            </Button>
          </div>

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {referrals.length === 0 ? (
              <div className="p-12 text-center">
                <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No Referrers Yet</h3>
                <p className="text-gray-500">Customers can create referral codes from the Refer a Friend page.</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-4 font-semibold text-gray-600">Code</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Referrer</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Referrals</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Sales</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Store Credit Earned</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Status</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map(ref => (
                    <tr key={ref.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-[#003456]">{ref.code}</span>
                          <button onClick={() => copyToClipboard(ref.code)} className="text-gray-400 hover:text-gray-600">
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{ref.referrer_name}</p>
                          <p className="text-sm text-gray-500">{ref.referrer_email}</p>
                        </div>
                      </td>
                      <td className="p-4">{ref.total_referrals || 0}</td>
                      <td className="p-4">${(ref.total_sales || 0).toFixed(2)}</td>
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="font-medium text-green-600">${(ref.store_credit_earned || ref.pending_referrer_credit || 0).toFixed(2)}</span>
                          {ref.store_credit_issued > 0 && (
                            <span className="text-xs text-gray-500">(${ref.store_credit_issued.toFixed(2)} issued)</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded text-xs font-medium ${
                          ref.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                        }`}>
                          {ref.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 flex-wrap">
                          <Button variant="ghost" size="sm" onClick={() => handleToggleReferral(ref)}>
                            {ref.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          {(ref.pending_referrer_credit > 0 || ref.store_credit_earned > 0) && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => {
                                setShowIssueCreditDialog(ref);
                                setCreditForm({ 
                                  amount: (ref.pending_referrer_credit || ref.store_credit_earned || 0).toString(), 
                                  reason: 'Refer-a-Friend reward' 
                                });
                              }}
                            >
                              <DollarSign className="h-4 w-4 mr-1" /> Issue Credit
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => handleSendCreditReminder(ref)}
                          >
                            <Mail className="h-4 w-4 mr-1" /> Send Reminder
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* Affiliate Program Tab */}
        <TabsContent value="affiliates">
          {/* Stats */}
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total Affiliates</p>
              <p className="text-2xl font-bold text-[#003456] mt-1">{affiliateStats?.total_affiliates || 0}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Approved</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{affiliateStats?.approved || 0}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Pending</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{affiliateStats?.pending || 0}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total Sales</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">${(affiliateStats?.total_sales || 0).toFixed(0)}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Pending Payouts</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">${(affiliateStats?.pending_payouts || 0).toFixed(2)}</p>
            </div>
          </div>

          {/* Current Rates Display */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-purple-600" />
                <span className="text-sm text-purple-800">
                  Affiliate Commission: <strong>{settings.affiliate_commission_percent}%</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-purple-600" />
                <span className="text-sm text-purple-800">
                  Customer Discount: <strong>{settings.affiliate_customer_discount}%</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-purple-600" />
                <span className="text-sm text-purple-800">
                  Cookie Duration: <strong>{settings.affiliate_cookie_days} days</strong>
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowSettingsDialog(true)}>
                Edit Rates
              </Button>
              <Button size="sm" className="bg-[#003456]" onClick={() => setShowCreateAffiliateDialog(true)} data-testid="create-affiliate-btn">
                <UserPlus className="h-4 w-4 mr-1" /> Create Affiliate
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {affiliates.length === 0 ? (
              <div className="p-12 text-center">
                <UserPlus className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No Affiliates Yet</h3>
                <p className="text-gray-500 mb-4">Affiliates can apply through the Affiliate Program page.</p>
                <a 
                  href="/affiliate" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#003456] hover:underline inline-flex items-center gap-1"
                >
                  View Affiliate Sign-up Page <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-4 font-semibold text-gray-600">Affiliate</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Code</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Commission</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Stats</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Earnings</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Status</th>
                    <th className="text-left p-4 font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {affiliates.map(affiliate => (
                    <tr key={affiliate.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{affiliate.name}</p>
                          <p className="text-sm text-gray-500">{affiliate.email}</p>
                          {affiliate.company_name && (
                            <p className="text-xs text-gray-400">{affiliate.company_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{affiliate.affiliate_code}</span>
                          <button onClick={() => copyToClipboard(affiliate.affiliate_code)} className="text-gray-400 hover:text-gray-600">
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="font-medium text-purple-600">{affiliate.commission_rate}%</span>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          <p><span className="text-gray-500">Clicks:</span> {affiliate.total_clicks || 0}</p>
                          <p><span className="text-gray-500">Conversions:</span> {affiliate.total_conversions || 0}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          <p className="font-medium text-green-600">${(affiliate.total_commission || 0).toFixed(2)}</p>
                          <p className="text-orange-600">${(affiliate.pending_commission || 0).toFixed(2)} pending</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded text-xs font-medium ${
                          affiliate.status === 'approved' ? 'bg-green-100 text-green-600' :
                          affiliate.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                          affiliate.status === 'suspended' ? 'bg-red-100 text-red-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {affiliate.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setShowAffiliateDetails(affiliate)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {affiliate.status === 'pending' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-green-600"
                                onClick={() => handleUpdateAffiliateStatus(affiliate.id, 'approved')}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600"
                                onClick={() => handleUpdateAffiliateStatus(affiliate.id, 'rejected')}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {affiliate.status === 'approved' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600"
                              onClick={() => handleUpdateAffiliateStatus(affiliate.id, 'suspended')}
                            >
                              Suspend
                            </Button>
                          )}
                          {affiliate.status === 'suspended' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-green-600"
                              onClick={() => handleUpdateAffiliateStatus(affiliate.id, 'approved')}
                            >
                              Reactivate
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns">
          <div className="space-y-6">
            {/* Campaign Stats */}
            <div className="grid md:grid-cols-5 gap-4">
              <div className="bg-white rounded-2xl p-5 shadow-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total Campaigns</p>
                <p className="text-2xl font-bold text-[#003456] mt-1">{campaignStats?.total_campaigns || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Active</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{campaignStats?.active_campaigns || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total Sales</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">${(campaignStats?.total_campaign_sales || 0).toFixed(0)}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Commissions</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">${(campaignStats?.total_campaign_commissions || 0).toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Discounts Given</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">${(campaignStats?.total_discounts_given || 0).toFixed(2)}</p>
              </div>
            </div>

            {/* Campaigns List */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <div>
                  <h3 className="font-semibold text-gray-700">All Affiliate Campaigns</h3>
                  <p className="text-sm text-gray-500">View campaign performance and customer details</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      await axios.post(`${API_URL}/api/affiliates/admin/campaigns/recalculate-stats`, {}, { headers });
                      toast.success('Campaign stats recalculated!');
                      fetchData();
                    } catch (e) {
                      toast.error('Failed to recalculate stats');
                    }
                  }}
                  data-testid="recalculate-stats-button"
                >
                  <RefreshCw className="h-4 w-4 mr-2" /> Recalculate Stats
                </Button>
              </div>
              
              {campaigns.length === 0 ? (
                <div className="p-12 text-center">
                  <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">No Campaigns Yet</h3>
                  <p className="text-gray-500">Affiliates can create campaigns from their dashboard.</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-4 font-medium text-gray-600">Campaign</th>
                      <th className="text-left p-4 font-medium text-gray-600">Affiliate</th>
                      <th className="text-left p-4 font-medium text-gray-600">Commission %</th>
                      <th className="text-left p-4 font-medium text-gray-600">Discount %</th>
                      <th className="text-left p-4 font-medium text-gray-600">Orders</th>
                      <th className="text-left p-4 font-medium text-gray-600">Sales</th>
                      <th className="text-left p-4 font-medium text-gray-600">Status</th>
                      <th className="text-left p-4 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id} className="border-t hover:bg-gray-50">
                        <td className="p-4">
                          <p className="font-medium">{campaign.campaign_name}</p>
                          <code className="text-xs text-gray-500">{campaign.campaign_code}</code>
                        </td>
                        <td className="p-4">
                          <p className="text-sm">{campaign.affiliate_name || '—'}</p>
                          <p className="text-xs text-gray-500">{campaign.affiliate_email || campaign.affiliate_code}</p>
                        </td>
                        <td className="p-4">
                          <span className="text-emerald-600 font-medium">{campaign.commission_percent}%</span>
                        </td>
                        <td className="p-4">
                          <span className="text-blue-600 font-medium">{campaign.discount_percent}%</span>
                        </td>
                        <td className="p-4 font-medium">{campaign.total_orders || 0}</td>
                        <td className="p-4 font-medium">${(campaign.total_sales || 0).toFixed(2)}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            campaign.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {campaign.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-4">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              try {
                                const res = await axios.get(
                                  `${API_URL}/api/affiliates/admin/campaigns/${campaign.id}/customers`,
                                  { headers }
                                );
                                setCampaignCustomers(res.data.customers || []);
                                setSelectedCampaign({ ...campaign, summary: res.data.summary });
                              } catch (error) {
                                toast.error('Failed to load customers');
                              }
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" /> Customers
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Points Override Section */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-700 mb-4">Affiliate Points Override</h3>
              <p className="text-sm text-gray-500 mb-4">
                Set custom points for specific affiliates (overrides tier-based points)
              </p>
              <div className="space-y-3">
                {affiliates.filter(a => a.status === 'approved').map(affiliate => (
                  <div key={affiliate.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{affiliate.name}</p>
                      <p className="text-sm text-gray-500">{affiliate.affiliate_code}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-600">
                        Current: <strong>{affiliate.points_override || 'Tier-based'}</strong>
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setShowPointsOverrideDialog(affiliate);
                          setPointsOverrideValue(affiliate.points_override || 10);
                        }}
                      >
                        {affiliate.points_override ? 'Edit Override' : 'Set Override'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Payouts Tab */}
        <TabsContent value="payouts">
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <p className="text-sm text-gray-500">Total Sales</p>
                <p className="text-3xl font-bold text-[#003456]">{salesStats?.total_sales || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <p className="text-sm text-gray-500">Pending Payouts</p>
                <p className="text-3xl font-bold text-orange-600">{salesStats?.pending_count || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <p className="text-sm text-gray-500">Cleared for Payout</p>
                <p className="text-3xl font-bold text-green-600">${(salesStats?.cleared_for_payout || 0).toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <p className="text-sm text-gray-500">Already Paid</p>
                <p className="text-3xl font-bold text-blue-600">{salesStats?.paid_count || 0}</p>
              </div>
            </div>

            {/* Actions Bar */}
            <div className="bg-white rounded-2xl shadow-sm p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  {selectedSales.length} sale(s) selected
                </span>
                <span className="text-sm text-gray-500">
                  Total: ${affiliateSales.filter(s => selectedSales.includes(s.id)).reduce((acc, s) => acc + (s.commission_amount || 0), 0).toFixed(2)}
                </span>
              </div>
              <Button 
                onClick={handleMarkSalesAsPaid}
                disabled={selectedSales.length === 0 || processingPayout}
                className="bg-green-600 hover:bg-green-700"
              >
                {processingPayout ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                ) : (
                  <><Check className="h-4 w-4 mr-2" /> Mark as Paid</>
                )}
              </Button>
            </div>

            {/* Sales Table */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-4 w-8">
                        <input 
                          type="checkbox"
                          checked={affiliateSales.filter(s => s.payout_status !== 'paid' && s.cleared_30_day_window).every(s => selectedSales.includes(s.id))}
                          onChange={(e) => {
                            const eligibleSales = affiliateSales.filter(s => s.payout_status !== 'paid' && s.cleared_30_day_window);
                            setSelectedSales(e.target.checked ? eligibleSales.map(s => s.id) : []);
                          }}
                          className="rounded border-gray-300"
                        />
                      </th>
                      <th className="text-left p-4 font-medium text-gray-600">Affiliate</th>
                      <th className="text-left p-4 font-medium text-gray-600">Order</th>
                      <th className="text-left p-4 font-medium text-gray-600">Total</th>
                      <th className="text-left p-4 font-medium text-gray-600">Commission</th>
                      <th className="text-left p-4 font-medium text-gray-600">Date</th>
                      <th className="text-left p-4 font-medium text-gray-600">Clearance</th>
                      <th className="text-left p-4 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affiliateSales.map((sale) => (
                      <tr key={sale.id} className={`border-t hover:bg-gray-50 ${sale.payout_status === 'paid' ? 'opacity-60' : ''}`}>
                        <td className="p-4">
                          {sale.payout_status !== 'paid' && sale.cleared_30_day_window && (
                            <input 
                              type="checkbox"
                              checked={selectedSales.includes(sale.id)}
                              onChange={(e) => {
                                setSelectedSales(prev => 
                                  e.target.checked 
                                    ? [...prev, sale.id]
                                    : prev.filter(id => id !== sale.id)
                                );
                              }}
                              className="rounded border-gray-300"
                            />
                          )}
                        </td>
                        <td className="p-4">
                          <p className="font-medium">{sale.affiliate_name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{sale.affiliate_code}</p>
                        </td>
                        <td className="p-4">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{sale.order_id?.slice(0, 8)}...</code>
                        </td>
                        <td className="p-4 font-medium">${(sale.order_total || 0).toFixed(2)}</td>
                        <td className="p-4 font-medium text-green-600">${(sale.commission_amount || 0).toFixed(2)}</td>
                        <td className="p-4 text-gray-500">
                          {sale.converted_at ? new Date(sale.converted_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="p-4">
                          {sale.cleared_30_day_window ? (
                            <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">Cleared</span>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs bg-amber-100 text-amber-700">
                              {sale.days_since_order || 0} days
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          {sale.payout_status === 'paid' ? (
                            <div>
                              <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">Paid</span>
                              {sale.payout_date && (
                                <p className="text-xs text-gray-500 mt-1">{new Date(sale.payout_date).toLocaleDateString()}</p>
                              )}
                            </div>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs bg-orange-100 text-orange-700">Pending</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {affiliateSales.length === 0 && (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-gray-500">
                          No affiliate sales recorded yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Store Credits Section */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-semibold text-gray-700">Store Credits</h3>
                  <p className="text-sm text-gray-500">Issue store credits for Refer-a-Friend rewards</p>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="text-gray-600">Total Issued: <strong className="text-[#003456]">${(storeCreditStats?.total_amount_issued || 0).toFixed(2)}</strong></span>
                  <span className="text-gray-600">Active: <strong className="text-green-600">${(storeCreditStats?.total_remaining_balance || 0).toFixed(2)}</strong></span>
                  <span className="text-gray-600">Used: <strong className="text-blue-600">${(storeCreditStats?.total_amount_used || 0).toFixed(2)}</strong></span>
                </div>
              </div>
              
              {storeCredits.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-600">Code</th>
                        <th className="text-left p-3 font-medium text-gray-600">Referrer</th>
                        <th className="text-left p-3 font-medium text-gray-600">Original</th>
                        <th className="text-left p-3 font-medium text-gray-600">Remaining</th>
                        <th className="text-left p-3 font-medium text-gray-600">Status</th>
                        <th className="text-left p-3 font-medium text-gray-600">Created</th>
                        <th className="text-left p-3 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storeCredits.map((credit) => (
                        <tr key={credit.id} className="border-t hover:bg-gray-50">
                          <td className="p-3">
                            <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">{credit.code}</code>
                          </td>
                          <td className="p-3">
                            <p className="font-medium">{credit.referrer_name || '—'}</p>
                            <p className="text-xs text-gray-500">{credit.referrer_email}</p>
                          </td>
                          <td className="p-3 font-medium">${(credit.amount || 0).toFixed(2)}</td>
                          <td className="p-3 font-medium text-green-600">${(credit.remaining_balance || 0).toFixed(2)}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              credit.is_active 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {credit.is_active ? 'Active' : 'Used'}
                            </span>
                          </td>
                          <td className="p-3 text-gray-500">
                            {credit.created_at ? new Date(credit.created_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="p-3">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => copyToClipboard(credit.code)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No store credits issued yet</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Media Library Tab */}
        <TabsContent value="media">
          <div className="space-y-6">
            {/* Media Stats & Actions */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Affiliate Media Library</h3>
                <p className="text-sm text-gray-500">Upload marketing assets for affiliates to use</p>
              </div>
              <Button onClick={() => setShowUploadDialog(true)} className="bg-[#003456]" data-testid="upload-media-btn">
                <Upload className="h-4 w-4 mr-2" /> Add Asset
              </Button>
            </div>

            {/* Media Requests Alert */}
            {mediaRequests.filter(r => r.status === 'pending').length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-yellow-800 font-medium mb-2">
                  <Send className="h-4 w-4" />
                  {mediaRequests.filter(r => r.status === 'pending').length} Pending Media Request(s)
                </div>
                <div className="space-y-2">
                  {mediaRequests.filter(r => r.status === 'pending').slice(0, 3).map(req => (
                    <div key={req.id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{req.description.substring(0, 60)}...</p>
                        <p className="text-xs text-gray-500">From: {req.affiliate_name || req.affiliate_email} • {req.urgency} urgency</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleUpdateRequestStatus(req.id, 'in_progress')}>
                          Start
                        </Button>
                        <Button size="sm" onClick={() => handleUpdateRequestStatus(req.id, 'completed')} className="bg-green-600">
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleUpdateRequestStatus(req.id, 'declined')}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Media Grid by Category */}
            {MEDIA_CATEGORIES.map(cat => {
              const categoryAssets = mediaAssets.filter(a => a.category === cat.id);
              if (categoryAssets.length === 0) return null;
              
              return (
                <div key={cat.id} className="bg-white rounded-2xl shadow-sm p-6">
                  <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <cat.icon className="h-5 w-5" />
                    {cat.name} ({categoryAssets.length})
                  </h4>
                  <div className="grid md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {categoryAssets.map(asset => (
                      <div key={asset.id} className="border rounded-xl overflow-hidden group hover:shadow-md transition-shadow">
                        {asset.file_type === 'image' && (
                          <div className="aspect-square bg-gray-100 relative">
                            <img src={asset.file_url} alt={asset.name} className="w-full h-full object-cover" />
                          </div>
                        )}
                        {asset.file_type === 'video' && (
                          <div className="aspect-square bg-gray-900 flex items-center justify-center">
                            <Video className="h-8 w-8 text-white/50" />
                          </div>
                        )}
                        {asset.file_type === 'pdf' && (
                          <div className="aspect-square bg-red-50 flex items-center justify-center">
                            <FileText className="h-8 w-8 text-red-500" />
                          </div>
                        )}
                        <div className="p-2">
                          <p className="font-medium text-xs truncate">{asset.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-gray-400">{asset.downloads || 0} downloads</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 w-6 p-0 text-red-500 hover:bg-red-50"
                              onClick={() => handleDeleteMedia(asset.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {mediaAssets.length === 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <Folder className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No media assets yet</p>
                <p className="text-sm text-gray-400 mt-1">Upload assets for your affiliates to use</p>
                <Button onClick={() => setShowUploadDialog(true)} className="mt-4 bg-[#003456]">
                  <Upload className="h-4 w-4 mr-2" /> Add First Asset
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Legal Documents Tab */}
        <TabsContent value="legal">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Affiliate Terms */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="h-6 w-6 text-[#003456]" />
                <h3 className="text-lg font-semibold">Affiliate Terms & Conditions</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                The legal agreement affiliates must accept before joining the program. Covers commission structure, payment terms, and prohibited activities.
              </p>
              <div className="flex gap-2">
                <a 
                  href={`${API_URL}/api/affiliates/legal/terms`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-[#003456] hover:underline"
                >
                  <Eye className="h-4 w-4" /> Preview
                </a>
              </div>
            </div>

            {/* FTC Disclosure */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="h-6 w-6 text-purple-600" />
                <h3 className="text-lg font-semibold">FTC Disclosure Requirements</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Guidelines for affiliates on properly disclosing their relationship when promoting products, as required by the FTC.
              </p>
              <div className="flex gap-2">
                <a 
                  href={`${API_URL}/api/affiliates/legal/disclosure`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-[#003456] hover:underline"
                >
                  <Eye className="h-4 w-4" /> Preview
                </a>
              </div>
            </div>

            {/* Customer Disclosure */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-semibold">Customer Affiliate Code Info</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Information displayed to customers explaining what happens when they use an affiliate code at checkout.
              </p>
              <div className="flex gap-2">
                <a 
                  href={`${API_URL}/api/affiliates/legal/customer-disclosure`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-[#003456] hover:underline"
                >
                  <Eye className="h-4 w-4" /> Preview
                </a>
              </div>
            </div>

            {/* Placeholder for Upload */}
            <div className="bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Download className="h-6 w-6 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-500">Upload Custom Documents</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Upload your own legal documents (PDF) that affiliates can download. Coming soon.
              </p>
              <Button disabled variant="outline" className="opacity-50">
                Upload Document
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Commission Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Commission Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            {/* Refer-a-Friend Settings */}
            <div className="border-b pb-4">
              <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Refer-a-Friend Program
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Customer Discount (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={settings.referral_discount_percent}
                    onChange={(e) => setSettings({ ...settings, referral_discount_percent: parseFloat(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-gray-400 mt-1">Discount for referred customers</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Referrer Store Credit (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={settings.referral_commission_percent}
                    onChange={(e) => setSettings({ ...settings, referral_commission_percent: parseFloat(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-gray-400 mt-1">Commission earned by referrer</p>
                </div>
              </div>
            </div>

            {/* Affiliate Program Settings */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Affiliate Program
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Affiliate Commission (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={settings.affiliate_commission_percent}
                    onChange={(e) => setSettings({ ...settings, affiliate_commission_percent: parseFloat(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-gray-400 mt-1">Default commission for new affiliates</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Customer Discount (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={settings.affiliate_customer_discount}
                    onChange={(e) => setSettings({ ...settings, affiliate_customer_discount: parseFloat(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-gray-400 mt-1">Discount when using affiliate code</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm text-gray-600">Cookie Duration (days)</label>
                  <Input
                    type="number"
                    min="1"
                    max="90"
                    value={settings.affiliate_cookie_days}
                    onChange={(e) => setSettings({ ...settings, affiliate_cookie_days: parseInt(e.target.value) || 30 })}
                  />
                  <p className="text-xs text-gray-400 mt-1">How long affiliate attribution lasts after initial click</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
              <Button className="bg-[#003456]" onClick={handleSaveSettings}>Save Settings</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Affiliate Details Dialog */}
      <Dialog open={!!showAffiliateDetails} onOpenChange={() => setShowAffiliateDetails(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Affiliate Details</DialogTitle>
          </DialogHeader>
          {showAffiliateDetails && (
            <div className="space-y-6 pt-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Contact Information</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-500">Name:</span> {showAffiliateDetails.name}</p>
                    <p><span className="text-gray-500">Email:</span> {showAffiliateDetails.email}</p>
                    {showAffiliateDetails.company_name && (
                      <p><span className="text-gray-500">Company:</span> {showAffiliateDetails.company_name}</p>
                    )}
                    {showAffiliateDetails.website && (
                      <p><span className="text-gray-500">Website:</span> {showAffiliateDetails.website}</p>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Performance</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-500">Total Clicks:</span> {showAffiliateDetails.total_clicks}</p>
                    <p><span className="text-gray-500">Conversions:</span> {showAffiliateDetails.total_conversions}</p>
                    <p><span className="text-gray-500">Conversion Rate:</span> {showAffiliateDetails.total_clicks > 0 ? ((showAffiliateDetails.total_conversions / showAffiliateDetails.total_clicks) * 100).toFixed(1) : 0}%</p>
                    <p><span className="text-gray-500">Total Sales:</span> ${(showAffiliateDetails.total_sales || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-3">Earnings</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-600">${(showAffiliateDetails.total_commission || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Total Earned</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">${(showAffiliateDetails.pending_commission || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Pending Payout</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">${(showAffiliateDetails.paid_commission || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Already Paid</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between border-t pt-4">
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-600">Commission Rate:</label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    className="w-20"
                    defaultValue={showAffiliateDetails.commission_rate}
                    onBlur={(e) => handleUpdateAffiliateCommission(showAffiliateDetails.id, parseFloat(e.target.value))}
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
                {showAffiliateDetails.pending_commission > 0 && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      handleProcessPayout(showAffiliateDetails.id, showAffiliateDetails.pending_commission);
                      setShowAffiliateDetails(null);
                    }}
                  >
                    <DollarSign className="h-4 w-4 mr-1" />
                    Process Payout (${showAffiliateDetails.pending_commission.toFixed(2)})
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Media Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Add Media Asset
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadMedia} className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asset Name *</label>
              <Input
                value={newMedia.name}
                onChange={(e) => setNewMedia({ ...newMedia, name: e.target.value })}
                placeholder="e.g., D-Freeze Logo - White"
                data-testid="media-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  value={newMedia.category}
                  onChange={(e) => setNewMedia({ ...newMedia, category: e.target.value })}
                  className="w-full p-2 border rounded-lg text-sm"
                >
                  {MEDIA_CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File Type *</label>
                <select
                  value={newMedia.file_type}
                  onChange={(e) => setNewMedia({ ...newMedia, file_type: e.target.value })}
                  className="w-full p-2 border rounded-lg text-sm"
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="pdf">PDF</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">File URL *</label>
              <Input
                value={newMedia.file_url}
                onChange={(e) => setNewMedia({ ...newMedia, file_url: e.target.value })}
                placeholder="https://example.com/assets/logo.png"
                data-testid="media-url-input"
              />
              <p className="text-xs text-gray-500 mt-1">Upload your file to cloud storage first, then paste the URL here</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={newMedia.description}
                onChange={(e) => setNewMedia({ ...newMedia, description: e.target.value })}
                placeholder="Brief description of the asset"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
              <Input
                value={newMedia.tags}
                onChange={(e) => setNewMedia({ ...newMedia, tags: e.target.value })}
                placeholder="logo, white, horizontal"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#003456]" disabled={uploading} data-testid="submit-media-btn">
                {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading...</> : 'Add Asset'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Campaign Customers Dialog */}
      <Dialog open={!!selectedCampaign} onOpenChange={() => { setSelectedCampaign(null); setCampaignCustomers([]); }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Campaign Customers: {selectedCampaign?.campaign_name}
            </DialogTitle>
          </DialogHeader>
          <div className="pt-4">
            {/* Campaign Summary Stats */}
            {selectedCampaign?.summary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">Total Customers</p>
                  <p className="text-xl font-bold text-[#003456]">{selectedCampaign.summary.total_customers || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">Total Sales</p>
                  <p className="text-xl font-bold text-[#003456]">${(selectedCampaign.summary.total_sales || 0).toFixed(2)}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-emerald-600">Commission</p>
                  <p className="text-xl font-bold text-emerald-600">${(selectedCampaign.summary.total_commission || 0).toFixed(2)}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-blue-600">Discounts</p>
                  <p className="text-xl font-bold text-blue-600">${(selectedCampaign.summary.total_discounts || 0).toFixed(2)}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-purple-600">Locations</p>
                  <p className="text-xl font-bold text-purple-600">{selectedCampaign.summary.unique_locations?.length || 0}</p>
                </div>
              </div>
            )}

            {/* Campaign Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Campaign Code:</span>
                  <p className="font-mono font-medium">{selectedCampaign?.campaign_code}</p>
                </div>
                <div>
                  <span className="text-gray-500">Commission Rate:</span>
                  <p className="font-medium text-emerald-600">{selectedCampaign?.commission_percent}%</p>
                </div>
                <div>
                  <span className="text-gray-500">Customer Discount:</span>
                  <p className="font-medium text-blue-600">{selectedCampaign?.discount_percent}%</p>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <p className={`font-medium ${selectedCampaign?.is_active ? 'text-green-600' : 'text-gray-500'}`}>
                    {selectedCampaign?.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </div>

            {/* Customers Table */}
            {campaignCustomers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium text-gray-600">Customer</th>
                      <th className="text-left p-3 font-medium text-gray-600">Email</th>
                      <th className="text-left p-3 font-medium text-gray-600">Location</th>
                      <th className="text-left p-3 font-medium text-gray-600">Order Total</th>
                      <th className="text-left p-3 font-medium text-gray-600">Commission</th>
                      <th className="text-left p-3 font-medium text-gray-600">Discount</th>
                      <th className="text-left p-3 font-medium text-gray-600">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignCustomers.map((customer) => (
                      <tr key={customer.id} className="border-t hover:bg-gray-50">
                        <td className="p-3 font-medium">{customer.customer_name}</td>
                        <td className="p-3 text-gray-600">{customer.customer_email}</td>
                        <td className="p-3">
                          {customer.customer_city && customer.customer_country 
                            ? `${customer.customer_city}, ${customer.customer_country}`
                            : customer.customer_location || '—'}
                        </td>
                        <td className="p-3 font-medium">${customer.order_total?.toFixed(2)}</td>
                        <td className="p-3 text-emerald-600">${customer.commission_amount?.toFixed(2)}</td>
                        <td className="p-3 text-blue-600">${customer.discount_amount?.toFixed(2)}</td>
                        <td className="p-3 text-gray-500">
                          {new Date(customer.purchased_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No customers yet for this campaign</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Points Override Dialog */}
      <Dialog open={!!showPointsOverrideDialog} onOpenChange={() => setShowPointsOverrideDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Points Override</DialogTitle>
          </DialogHeader>
          <div className="pt-4 space-y-4">
            <p className="text-sm text-gray-600">
              Set a custom points allocation for <strong>{showPointsOverrideDialog?.name}</strong>.
              This will override their tier-based points.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Points: {pointsOverrideValue}
              </label>
              <input
                type="range"
                min="10"
                max="20"
                step="1"
                value={pointsOverrideValue}
                onChange={(e) => setPointsOverrideValue(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#003456]"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>10 (Base)</span>
                <span>15 (Max Tier)</span>
                <span>20 (Extended)</span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              {showPointsOverrideDialog?.points_override && (
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:bg-red-50"
                  onClick={async () => {
                    try {
                      await axios.put(
                        `${API_URL}/api/affiliates/admin/${showPointsOverrideDialog.id}/points-override`,
                        {},
                        { headers }
                      );
                      toast.success('Points override removed');
                      setShowPointsOverrideDialog(null);
                      fetchData();
                    } catch (error) {
                      toast.error('Failed to remove override');
                    }
                  }}
                >
                  Remove Override
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowPointsOverrideDialog(null)}>
                Cancel
              </Button>
              <Button 
                className="bg-[#003456]"
                onClick={async () => {
                  try {
                    await axios.put(
                      `${API_URL}/api/affiliates/admin/${showPointsOverrideDialog.id}/points-override?points=${pointsOverrideValue}`,
                      {},
                      { headers }
                    );
                    toast.success(`Points override set to ${pointsOverrideValue}`);
                    setShowPointsOverrideDialog(null);
                    fetchData();
                  } catch (error) {
                    toast.error('Failed to set override');
                  }
                }}
              >
                Save Override
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Affiliate Dialog */}
      <Dialog open={showCreateAffiliateDialog} onOpenChange={(open) => { if (!open) resetAffiliateForm(); else setShowCreateAffiliateDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-[#003456]" />
              Create New Affiliate
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAffiliate} className="space-y-6 pt-4">
            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 border-b pb-2">Contact Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <Input
                    value={affiliateForm.name}
                    onChange={(e) => setAffiliateForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Doe"
                    required
                    data-testid="affiliate-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <Input
                    type="email"
                    value={affiliateForm.email}
                    onChange={(e) => setAffiliateForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="affiliate@example.com"
                    required
                    data-testid="affiliate-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                  <Input
                    value={affiliateForm.city}
                    onChange={(e) => setAffiliateForm(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="New York"
                    required
                    data-testid="affiliate-city-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Province / State *</label>
                  <Input
                    value={affiliateForm.province_state}
                    onChange={(e) => setAffiliateForm(prev => ({ ...prev, province_state: e.target.value }))}
                    placeholder="NY"
                    required
                    data-testid="affiliate-state-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <Input
                  type="tel"
                  value={affiliateForm.phone}
                  onChange={(e) => setAffiliateForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+1 (555) 123-4567"
                  required
                  data-testid="affiliate-phone-input"
                />
              </div>
            </div>

            {/* Compliance Opt-ins */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 border-b pb-2">Compliance & Agreements</h3>
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={affiliateForm.affiliate_program_optin}
                    onChange={(e) => setAffiliateForm(prev => ({ ...prev, affiliate_program_optin: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                    data-testid="affiliate-program-optin"
                  />
                  <div>
                    <span className="font-medium text-gray-800">Affiliate Program Agreement *</span>
                    <p className="text-sm text-gray-500">Affiliate has agreed to the affiliate program terms and conditions</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={affiliateForm.ftc_optin}
                    onChange={(e) => setAffiliateForm(prev => ({ ...prev, ftc_optin: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                    data-testid="affiliate-ftc-optin"
                  />
                  <div>
                    <span className="font-medium text-gray-800">FTC Disclosure Agreement</span>
                    <p className="text-sm text-gray-500">Affiliate agrees to comply with FTC disclosure requirements</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={affiliateForm.bureau_optin}
                    onChange={(e) => setAffiliateForm(prev => ({ ...prev, bureau_optin: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                    data-testid="affiliate-bureau-optin"
                  />
                  <div>
                    <span className="font-medium text-gray-800">Bureau Opt-in</span>
                    <p className="text-sm text-gray-500">Affiliate consents to bureau-related communications</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Tier & Commission Settings */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 border-b pb-2">Tier & Commission Settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Affiliate Tier *</label>
                  <select
                    value={affiliateForm.tier}
                    onChange={(e) => {
                      const tier = parseInt(e.target.value);
                      // Auto-set commission rate based on tier
                      const tierPoints = { 1: 10, 2: 11, 3: 12, 4: 13, 5: 15 };
                      setAffiliateForm(prev => ({ 
                        ...prev, 
                        tier,
                        commission_rate: tierPoints[tier] || 10
                      }));
                    }}
                    className="w-full p-3 border rounded-lg bg-white"
                    data-testid="affiliate-tier-select"
                  >
                    <option value={1}>Tier 1 - Bronze (10 points)</option>
                    <option value={2}>Tier 2 - Silver (11 points)</option>
                    <option value={3}>Tier 3 - Gold (12 points)</option>
                    <option value={4}>Tier 4 - Platinum (13 points)</option>
                    <option value={5}>Tier 5 - Diamond (15 points)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Higher tiers get more marketing credits for messages</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Commission Rate (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="20"
                    value={affiliateForm.commission_rate}
                    onChange={(e) => setAffiliateForm(prev => ({ ...prev, commission_rate: parseFloat(e.target.value) || 0 }))}
                    data-testid="affiliate-commission-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default commission percentage for this affiliate</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={resetAffiliateForm}>Cancel</Button>
              <Button 
                type="submit" 
                className="bg-[#003456]" 
                disabled={creatingAffiliate || !affiliateForm.affiliate_program_optin}
                data-testid="save-affiliate-btn"
              >
                {creatingAffiliate ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...</>
                ) : (
                  <><UserPlus className="h-4 w-4 mr-2" /> Create Affiliate</>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Issue Store Credit Dialog */}
      <Dialog open={!!showIssueCreditDialog} onOpenChange={() => { setShowIssueCreditDialog(null); setCreditForm({ amount: '', reason: 'Refer-a-Friend reward' }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Issue Store Credit
            </DialogTitle>
          </DialogHeader>
          {showIssueCreditDialog && (
            <div className="pt-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium">{showIssueCreditDialog.referrer_name}</p>
                <p className="text-sm text-gray-500">{showIssueCreditDialog.referrer_email}</p>
                <div className="mt-2 text-sm">
                  <span className="text-gray-500">Pending Commission: </span>
                  <span className="font-medium text-orange-600">${(showIssueCreditDialog.pending_commission || 0).toFixed(2)}</span>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Credit Amount</label>
                  <Input 
                    type="number" 
                    step="0.01"
                    placeholder="Enter amount"
                    value={creditForm.amount}
                    onChange={(e) => setCreditForm(prev => ({ ...prev, amount: e.target.value }))}
                    data-testid="store-credit-amount-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <Input 
                    placeholder="Reason for credit"
                    value={creditForm.reason}
                    onChange={(e) => setCreditForm(prev => ({ ...prev, reason: e.target.value }))}
                    data-testid="store-credit-reason-input"
                  />
                </div>
              </div>
              
              <p className="text-sm text-gray-500">
                A unique store credit code will be generated and emailed to the referrer. They can use this code at checkout to redeem their credit.
              </p>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => { setShowIssueCreditDialog(null); setCreditForm({ amount: '', reason: 'Refer-a-Friend reward' }); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleIssueStoreCredit(
                    showIssueCreditDialog.id, 
                    showIssueCreditDialog.referrer_email, 
                    showIssueCreditDialog.referrer_name
                  )}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="issue-store-credit-button"
                >
                  <Mail className="h-4 w-4 mr-2" /> Issue & Email Code
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReferralAffiliateManager;
